import cn from 'classnames'
import {FC, ReactNode} from 'react'

import {Section} from '@/components/common/content/Section'
import {TrustpilotWidget} from '@/components/trustpilot/TrustpilotWidget'
import {TrustpilotWidgetVariant} from '@/content/trustpilot'
import {theme} from '@/theme/theme'
import {DESKTOP_SCREENSIZE, MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

type TrustpilotCardProps = {
  children: ReactNode
  header?: ReactNode
  backgroundColor?: string
  hasBoxShadow?: boolean
  boxShadowColor?: string
  backgroundOverlay?: {color: string; heightRatio: number}
  hasNarrowPadding?: boolean
  marginTop?: string
}

export const TrustpilotCard: FC<TrustpilotCardProps> = ({
  children,
  header,
  backgroundColor = theme.palette.background.default,
  hasBoxShadow,
  boxShadowColor = 'rgba(229, 106, 97, 0.15)',
  backgroundOverlay,
  hasNarrowPadding = false,
  marginTop,
}) => (
  <>
    <Section marginTop={marginTop}>
      <div
        className={cn(
          'card',
          backgroundOverlay && 'background-overlay',
          hasNarrowPadding && 'narrow',
        )}
      >
        {header}
        <div className="content">
          {children}
          <div className="trustpilot">
            <TrustpilotWidget variant={TrustpilotWidgetVariant.stars} />
          </div>
        </div>
      </div>
    </Section>

    <style jsx>{`
      .card {
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing[2]};
        padding: ${theme.spacing[5]} ${theme.spacing[7.5]};
        background-color: ${backgroundColor};
        border-radius: 16px;
        ${hasBoxShadow ? `box-shadow: 0 15px 50px 0 ${boxShadowColor};` : ''}
      }
      .narrow {
        padding: ${theme.spacing[5]} ${theme.spacing[3]} ${theme.spacing[4]};
      }
      .background-overlay::before {
        content: '';
        position: absolute;
        display: block;
        top: 0;
        left: 0;
        width: 100%;
        height: ${(backgroundOverlay?.heightRatio ?? 1) * 100}%;
        background-color: ${backgroundOverlay?.color ?? theme.palette.background.default};
        z-index: -1;
      }
      .content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: ${theme.spacing[7]};
      }
      .trustpilot {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        max-width: 364px;
        height: 64px;
        padding-left: ${theme.spacing[7]};
        border-left: 1px solid ${theme.palette.gray[200]};
      }
      @media ${DESKTOP_SCREENSIZE} {
        .narrow .content {
          gap: 0;
        }
        .narrow .trustpilot {
          max-width: 340px;
          padding-left: ${theme.spacing[4]};
        }
      }
      @media ${TABLET_SCREENSIZE} {
        .card {
          padding: ${theme.spacing[5]};
        }
        .narrow {
          padding: ${theme.spacing[5]} ${theme.spacing[3]};
        }
        .content {
          flex-direction: column;
          gap: ${theme.spacing[5]};
        }
        .trustpilot {
          max-width: 100%;
          height: inherit;
          padding-top: ${theme.spacing[5]};
          padding-left: 0;
          border-top: 1px solid ${theme.palette.gray[200]};
          border-left: 0;
        }
      }
      @media ${MOBILE_SCREENSIZE} {
        .card {
          padding: ${theme.spacing[4]};
        }
        .narrow {
          padding: ${theme.spacing[4]} ${theme.spacing[3]};
        }
        .content {
          gap: ${theme.spacing[4]};
        }
        .trustpilot {
          padding-top: ${theme.spacing[4]};
        }
      }
    `}</style>
  </>
)
